/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useRef} from 'react';
import {useJobCreate} from '@ion/applications/src/components/jobs/useJobCreate';
import {notifyOnCreatedJob} from './signals/createJobNotifications';
import {liveJobsEnabled} from './signals/liveUpdates';
import {watch} from './WatchedJobs';

/**
 * Applies the Create Job dialog's notification toggle to the job it created.
 *
 * This cannot live in the dialog. `addJob` dispatches `JOB_CREATE_RESPONSE` and
 * hides the editor in the same tick, so the dialog is unmounted before the id
 * it would need exists. The toggle publishes to `notifyOnCreatedJob` instead
 * and this hook - called from a component which outlives the dialog - does the
 * watching.
 *
 * `jobCreateResult` stays in redux after the dialog closes and is not cleared
 * when it reopens, so the last handled id is remembered: without that, closing
 * and reopening the dialog would re-watch the previous job.
 *
 * ```typescript
 *
 *   import {useAutoWatchCreatedJob} from '.../useAutoWatchCreatedJob';
 *
 *   useAutoWatchCreatedJob();
 *
 * ```
 */
export const useAutoWatchCreatedJob = (): void => {
  const {jobCreateResult} = useJobCreate();
  const handled           = useRef<number>(0);
  const jobId             = Number(jobCreateResult?.id);

  useEffect(() => {
    const fresh = jobId && !isNaN(jobId) && handled.current !== jobId;

    if (liveJobsEnabled.value && fresh) {
      handled.current = jobId;

      if (notifyOnCreatedJob.value) {
        watch(jobId);
      }
    }
  }, [jobId]);
};

export default useAutoWatchCreatedJob;
